import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { Entity, IRelatedEntity, RelatedEntity } from '@app/core/model/entities/entity';
import { AbstractFieldBuilder } from '@app/shared/components/fields/abstract.field';
import {
  MatSelectEntityAutocomplete
} from '@app/shared/components/mat-select-autocomplete/mat-select-entity-autocomplete/mat-select-entity-autocomplete.component';
import { getValue } from '@app/shared/extra/utils';
import { BehaviorSubject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  template: '',
  standalone: false
})
// eslint-disable-next-line @angular-eslint/component-class-suffix
export abstract class EntityFieldBuilder<IT extends IRelatedEntity, T extends RelatedEntity>
  extends AbstractFieldBuilder<FormControl<T | T[]>> implements OnInit, AfterViewInit {
  @ViewChild('inputField') public inputField: MatSelectEntityAutocomplete<T, Entity>;

  public entityInitValue: T | T[];

  public allowedEntityIds: string[] = null;

  /**
   * Instantiate a RelatedEntity of <T> from an Object satisfying the RelatedEntity interface of <IT>
   * @param relatedEntity
   */
  protected abstract newRelatedEntity(relatedEntity: IT): T;

  /**
   * Navigate to the sheet of the related Entity
   */
  public abstract navigateToEntitySheet(): Promise<void>;

  /**
   * Type guard to ensure the control type is not an array.
   */
  protected isSingleEntityControl(control: FormControl<T | T[]>): control is FormControl<T> {
    return !Array.isArray(this.form.controls.field.value);
  }

  /**
   * Initialize form, fetch accessible Entities then set the field's initial value based on the current entity's
   * RelatedEntity
   */
  public ngOnInit(): void {
    this.setEntityInitValue();

    // Init form
    this.form = new FormGroup({
      field: new FormControl(this.entityInitValue, this.computeValidators(), this.computeAsyncValidators())
    });

    // Init value with data from the entity while Entities are loading
    this.setFieldValue(this.entityInitValue ?? null);
    this.setFieldInitialValue(this.entityInitValue ?? null);
    this.getNextState();

    // Set allowed entities filter
    const allowedEntitiesFilter: string = this.fieldConfig.customOptions?.allowedEntitiesFilter;
    if(allowedEntitiesFilter) {
      this.allowedEntityIds = getValue(this.appManager.currentEntity, allowedEntitiesFilter.split(".")) ?? [];
    }
  }

  /**
   * Set the init value of the entity according to the init value of the field.
   */
  protected setEntityInitValue(): void {
    if (this.entityInitValue || !this.fieldInitValue) return;

    this.entityInitValue = this.fieldInitValue?.let((relatedEntity: IT) => this.newRelatedEntity(relatedEntity)) ?? null;
  }

  /**
   * Setup hook for other fields to know when a new Entity is selected.
   */
  public ngAfterViewInit(): void {
    this.setupHooks();
    this.form.controls.field.valueChanges.pipe(
      distinctUntilChanged(),
      debounceTime(50),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      this.setFieldValue(value);
    });
    this.asyncPreconditionsForEdition$
      .subscribe(valid => {
        this.preconditionsForEdition = this.preconditionsForEdition && valid;
      });
  }

  /**
   * Instantiate a {@link BehaviorSubject} to track this field changes from the {@link FormStateService}.
   */
  protected initialiseValueChangeSubject(): void {
    this.formStateService.setPath(
      ['changes', this.fieldConfig.fieldCode],
      new BehaviorSubject({path: this.fieldConfig.fieldPath, value: this.entityInitValue})
    );
  }

  /**
   * Cancel edition and restore initial related Entity value.
   */
  public cancel(): void {
    super.cancel();
    this.form.controls.field.setValue(this.entityInitValue);
  }

  /**
   * Set focus on the element
   */
  public focus(): void {
    if (this.focusable) {
      setTimeout(() => {
        this.inputField?.onFocusIn();
      });
    }
  }
}
